/**
 * Fleet Packs motion. Text and controls never move. Only image planes and
 * pointer-inert decoration animate; every component is usable without JS.
 */
(() => {
  if (window.FleetPacksMotion) {
    window.FleetPacksMotion.init(document);
    return;
  }

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)');
  const editor = Boolean(window.Shopify?.designMode) || document.documentElement.classList.contains('shopify-design-mode');
  const surfaces = new Map();
  const visible = new Set();
  const pendingReveals = new Set();
  const builderEligible = new Set();
  const builderTimers = new Map();
  const brandingTyping = new Map();
  const revealSelector = '.fp-reveal, .fp-stagger, .fp-process__step, .fp-builder__tile';
  const definitions = [
    ['.fp-hero__media', 'hero'],
    ['.fp-showcase__media, .fp-collection-card__media', 'product'],
    ['media-gallery .product-media-container--image .product-media', 'product'],
    ['.fp-feature .fp-media, .fp-page-hero__media .fp-media', 'photo'],
    ['.fp-card', 'card'],
    ['.fp-builder__tile', 'tile'],
    ['.fp-video-feature__frame', 'video'],
  ];
  let frame = 0;
  let dirtyGeometry = true;
  let pointerState = null;
  const enabled = () => !reduced.matches && !editor;
  const depthEnabled = () => enabled() && finePointer.matches && !document.hidden;
  const select = (root, selector) => [
    ...(root instanceof Element && root.matches(selector) ? [root] : []),
    ...(root.querySelectorAll?.(selector) || []),
  ];

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const state = surfaces.get(entry.target);
      if (state) {
        state.visible = entry.isIntersecting;
        state.node.classList.toggle('fp-motion-visible', state.visible);
        if (state.visible) {
          visible.add(state);
          state.node.classList.add('fp-motion-entered');
        } else {
          visible.delete(state);
          reset(state);
        }
      }
      if (entry.isIntersecting && pendingReveals.has(entry.target) && !entry.target.matches('.fp-builder__tile')) {
        entry.target.classList.add('is-revealed');
        pendingReveals.delete(entry.target);
        if (!state) observer.unobserve(entry.target);
      }
    }
    schedule(true);
  }, { threshold: 0, rootMargin: '0px 0px -3% 0px' }) : null;

  const brandingObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) startBrandTyping(brandingTyping.get(entry.target));
    }
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }) : null;

  function finishBrandTyping(state) {
    if (!state) return;
    if (state.frame) window.cancelAnimationFrame(state.frame);
    brandingObserver?.unobserve(state.node);
    state.node.classList.remove('is-type-ready');
    for (const item of state.items) {
      item.overlay?.remove();
      item.overlay = null;
      item.title.classList.remove('is-typing');
      item.title.classList.add('is-typed');
    }
    state.done = true;
    state.running = false;
  }

  function startBrandTyping(state) {
    if (!state || state.running || state.done || !enabled()) return;
    state.running = true;
    brandingObserver?.unobserve(state.node);
    const millisecondsPerCharacter = 28;
    let nextStart = 0;
    for (const item of state.items) {
      item.start = nextStart;
      item.end = nextStart + item.characters.length * millisecondsPerCharacter;
      nextStart = item.end + 180;
    }
    const startTime = window.performance.now();

    function tick(now) {
      if (!enabled() || !state.node.isConnected) {
        finishBrandTyping(state);
        return;
      }
      const elapsed = now - startTime;
      for (const item of state.items) {
        if (elapsed < item.start || item.title.classList.contains('is-typed')) continue;
        if (elapsed >= item.end) {
          item.title.classList.add('is-typed');
          item.title.classList.remove('is-typing');
          item.overlay?.remove();
          item.overlay = null;
          continue;
        }
        if (!item.overlay) {
          item.overlay = document.createElement('span');
          item.overlay.className = 'fp-feature__type-overlay';
          item.overlay.setAttribute('aria-hidden', 'true');
          item.overlay.style.color = item.color;
          item.title.classList.add('is-typing');
          item.title.appendChild(item.overlay);
        }
        const count = Math.min(item.characters.length, Math.floor((elapsed - item.start) / millisecondsPerCharacter) + 1);
        if (count !== item.count) {
          item.overlay.textContent = item.characters.slice(0, count).join('');
          item.count = count;
        }
      }
      if (elapsed < nextStart) state.frame = window.requestAnimationFrame(tick);
      else finishBrandTyping(state);
    }
    state.frame = window.requestAnimationFrame(tick);
  }

  function initBrandTyping(root) {
    if (!enabled() || !brandingObserver) return;
    for (const list of select(root, '.fp-feature--branding-motion .fp-feature__list')) {
      if (brandingTyping.has(list)) continue;
      const items = [...list.querySelectorAll('.fp-feature__item-title')].map((title) => ({
        title,
        characters: Array.from(title.textContent.trim()),
        color: window.getComputedStyle(title).color,
        count: 0,
        overlay: null,
      }));
      if (!items.length) continue;
      brandingTyping.set(list, { node: list, items, frame: 0, running: false, done: false });
      list.classList.add('is-type-ready');
      brandingObserver.observe(list);
    }
  }

  function revealNextBuilder(group) {
    if (builderTimers.has(group)) return;
    const tiles = [...group.querySelectorAll(':scope > .fp-builder__tile')];
    const next = tiles.find((tile) => pendingReveals.has(tile));
    if (!next || !builderEligible.has(next)) return;
    next.classList.add('is-revealed');
    pendingReveals.delete(next);
    builderEligible.delete(next);
    builderObserver?.unobserve(next);
    if (tiles.some((tile) => pendingReveals.has(tile) && builderEligible.has(tile))) {
      builderTimers.set(group, window.setTimeout(() => {
        builderTimers.delete(group);
        revealNextBuilder(group);
      }, 320));
    }
  }

  // A fast scroll can expose several cards at once; always reveal in order.
  const builderObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    const groups = new Set();
    for (const entry of entries) {
      if (!entry.isIntersecting || !pendingReveals.has(entry.target)) continue;
      const group = entry.target.closest('.fp-builder__visual');
      if (!group) continue;
      for (const tile of group.querySelectorAll(':scope > .fp-builder__tile')) {
        if (pendingReveals.has(tile)) builderEligible.add(tile);
        if (tile === entry.target) break;
      }
      groups.add(group);
    }
    for (const group of groups) revealNextBuilder(group);
  }, { threshold: 0, rootMargin: '0px 0px -35% 0px' }) : null;

  function reset(state) {
    state.hover = false;
    state.node.classList.remove('fp-motion-hover');
    for (const property of ['--fp-light-x', '--fp-light-y', '--fp-tilt-x', '--fp-tilt-y', '--fp-plane-scale']) {
      state.node.style.removeProperty(property);
    }
    if (pointerState === state) pointerState = null;
  }

  function register(node, type) {
    if (surfaces.has(node) || node.closest('dialog, .dialog-zoomed-gallery, quick-add-dialog')) return;
    if ((type === 'photo' || type === 'product') && !node.querySelector(':scope > img')) return;
    const state = { node, type, visible: false, hover: false, focus: false, rect: null, x: 0.5, y: 0.5, px: 0.5, py: 0.5 };
    surfaces.set(node, state);
    node.dataset.fpDepth = type;
    // Hero media is behind its scrim; listen on the existing containing hero.
    const target = type === 'hero' ? node.closest('.fp-hero') : node;
    const abort = new AbortController();
    state.abort = abort;
    const options = { passive: true, signal: abort.signal };
    target.addEventListener('pointerenter', (event) => {
      if (!depthEnabled() || event.pointerType === 'touch') return;
      state.rect = node.getBoundingClientRect();
      state.hover = true;
      node.classList.add('fp-motion-hover');
      if (['hero', 'photo', 'product'].includes(type)) { pointerState = state; move(event); }
    }, options);
    function move(event) {
      if (!['hero', 'photo', 'product'].includes(type) || !state.hover || !depthEnabled() || event.pointerType === 'touch') return;
      const rect = state.rect;
      if (!rect?.width || !rect.height) return;
      state.x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      state.y = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
      pointerState = state;
      schedule();
    }
    target.addEventListener('pointermove', move, options);
    target.addEventListener('pointerleave', () => { reset(state); schedule(true); }, options);
    target.addEventListener('pointercancel', () => reset(state), options);
    // Product inspection and video playback keep their original, stable targets.
    target.addEventListener('pointerdown', () => reset(state), options);
    if (observer) observer.observe(node);
    else { state.visible = true; visible.add(state); node.classList.add('fp-motion-entered', 'fp-motion-visible'); }
  }

  function init(root = document) {
    document.documentElement.classList.toggle('fp-motion-enabled', enabled());
    initBrandTyping(root);
    for (const group of select(root, '.fp-stagger')) {
      [...group.children].forEach((child, index) => {
        const interval = group.matches('.fp-process__steps') ? 260 : 90;
        child.style.setProperty('--fp-motion-delay', `${Math.min(index, 6) * interval}ms`);
      });
    }
    for (const element of select(root, revealSelector)) {
      if (element.classList.contains('is-revealed') || pendingReveals.has(element)) continue;
      if (!enabled() || !observer) element.classList.add('is-revealed');
      else {
        pendingReveals.add(element);
        if (element.matches('.fp-builder__tile')) builderObserver.observe(element);
        else observer.observe(element);
      }
    }
    for (const [selector, type] of definitions) {
      for (const node of select(root, selector)) register(node, type);
    }
    schedule(true);
  }

  function destroy(root) {
    for (const [list, state] of brandingTyping) {
      if (list !== root && !root.contains(list)) continue;
      finishBrandTyping(state);
      brandingTyping.delete(list);
    }
    for (const [group, timer] of builderTimers) {
      if (group !== root && !root.contains(group)) continue;
      window.clearTimeout(timer);
      builderTimers.delete(group);
    }
    for (const [node, state] of surfaces) {
      if (node !== root && !root.contains(node)) continue;
      state.abort.abort();
      reset(state);
      node.classList.remove('fp-motion-visible');
      observer?.unobserve(node);
      visible.delete(state);
      surfaces.delete(node);
    }
    for (const node of pendingReveals) {
      if (node === root || root.contains(node)) {
        observer?.unobserve(node);
        builderObserver?.unobserve(node);
        builderEligible.delete(node);
        pendingReveals.delete(node);
      }
    }
  }

  function schedule(geometry = false) {
    dirtyGeometry ||= geometry;
    if (!frame && !document.hidden) frame = window.requestAnimationFrame(update);
  }

  function update() {
    frame = 0;
    if (!depthEnabled()) return;
    const height = window.innerHeight;
    // Batch layout reads before writes. Scroll processes only visible media.
    if (dirtyGeometry) {
      for (const state of visible) {
        if (['hero', 'photo', 'product'].includes(state.type)) state.rect = state.node.getBoundingClientRect();
      }
    }
    for (const state of visible) {
      if (!state.rect || !['hero', 'photo', 'product'].includes(state.type)) continue;
      const progress = Math.max(0, Math.min(1, (height - state.rect.top) / (height + state.rect.height)));
      const active = state.hover && pointerState === state;
      if (active) {
        state.px += (state.x - state.px) * 0.22;
        state.py += (state.y - state.py) * 0.22;
      }
      const x = active ? state.px * 100 : 42 + progress * 16;
      const y = active ? state.py * 100 : 20 + progress * 50;
      state.node.style.setProperty('--fp-light-x', `${x.toFixed(2)}%`);
      state.node.style.setProperty('--fp-light-y', `${y.toFixed(2)}%`);
      if (state.type !== 'hero') {
        // Inward scaling keeps all corners inside the original media rectangle.
        state.node.style.setProperty('--fp-tilt-x', `${active ? ((0.5 - state.py) * 1.6).toFixed(3) : 0}deg`);
        state.node.style.setProperty('--fp-tilt-y', `${active ? ((state.px - 0.5) * 1.6).toFixed(3) : 0}deg`);
        state.node.style.setProperty('--fp-plane-scale', active ? '0.985' : '1');
      }
    }
    dirtyGeometry = false;
    if (pointerState?.hover && (Math.abs(pointerState.x - pointerState.px) > 0.002 || Math.abs(pointerState.y - pointerState.py) > 0.002)) schedule();
  }

  function preferencesChanged() {
    document.documentElement.classList.toggle('fp-motion-enabled', enabled());
    if (enabled()) initBrandTyping(document);
    else for (const state of brandingTyping.values()) finishBrandTyping(state);
    for (const state of surfaces.values()) reset(state);
    if (!enabled()) {
      for (const node of pendingReveals) {
        node.classList.add('is-revealed');
        if (!surfaces.has(node)) observer?.unobserve(node);
        builderObserver?.unobserve(node);
      }
      pendingReveals.clear();
      builderEligible.clear();
      for (const timer of builderTimers.values()) window.clearTimeout(timer);
      builderTimers.clear();
    }
    schedule(true);
  }

  function focusChanged(event) {
    for (const state of surfaces.values()) {
      const owner = state.node.closest('a, .product-media-container') || state.node;
      const focused = owner.contains(event.target);
      state.focus = focused;
      state.node.classList.toggle('fp-motion-focus', focused);
    }
    // An offscreen keyboard target never waits for an entrance animation.
    for (const node of pendingReveals) {
      if (node.contains(event.target)) {
        node.classList.add('is-revealed');
        pendingReveals.delete(node);
        if (!surfaces.has(node)) observer?.unobserve(node);
        builderObserver?.unobserve(node);
        builderEligible.delete(node);
      }
    }
  }

  window.FleetPacksMotion = { init, destroy };
  init();
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  // Horizon scrolls .page-wrapper, so capture nested scroll events too.
  document.addEventListener('scroll', () => schedule(true), { passive: true, capture: true });
  window.addEventListener('resize', () => schedule(true), { passive: true });
  document.addEventListener('focusin', focusChanged);
  document.addEventListener('focusout', (event) => {
    if (event.relatedTarget) return;
    for (const state of surfaces.values()) state.node.classList.remove('fp-motion-focus');
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      window.cancelAnimationFrame(frame); frame = 0;
      for (const state of surfaces.values()) reset(state);
    } else schedule(true);
  });
  reduced.addEventListener('change', preferencesChanged);
  finePointer.addEventListener('change', preferencesChanged);

  // Section rendering, filtering and variant changes can replace DOM at runtime.
  if ('MutationObserver' in window && document.body) {
    new MutationObserver((records) => {
      const added = new Set();
      const removed = new Set();
      for (const record of records) {
        for (const node of record.addedNodes) if (node instanceof HTMLElement) added.add(node);
        for (const node of record.removedNodes) if (node instanceof HTMLElement) removed.add(node);
      }
      for (const node of removed) if (!node.isConnected) destroy(node);
      for (const node of added) if (node.isConnected) init(node);
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
